import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Avatar,
  Divider,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SpotlightCard from '../Common/SpotlightCard';

import MenuIcon from '@mui/icons-material/Menu';
import MovieIcon from '@mui/icons-material/Movie';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // 🔐 Auth pages check
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/register';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: scrolled ? 'rgba(15, 15, 35, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.1)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* LOGO */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MovieIcon sx={{ fontSize: 32, color: '#6366f1' }} />
          <Typography
            variant="h5"
            component={Link}
            to={user ? '/' : '/login'}
            sx={{
              textDecoration: 'none',
              fontWeight: 800,
              background: 'linear-gradient(135deg,#6366f1,#ec4899)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            MovieDB
          </Typography>
        </Box>

        {/* ================= DESKTOP ================= */}
        {!isMobile ? (
          <>
            {/* NAV LINKS (HIDDEN ON LOGIN / REGISTER) */}
            {!isAuthPage && user && (
              <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, ml: 4 }}>
                <SpotlightCard
                  as={Link}
                  to="/"
                  style={navStyle}
                >
                  Home
                </SpotlightCard>

                <SpotlightCard
                  as={Link}
                  to="/search"
                  style={{ ...navStyle, display: 'flex', gap: 6 }}
                >
                  <SearchIcon sx={{ fontSize: 18 }} />
                  Search
                </SpotlightCard>

                {isAdmin() && (
                  <SpotlightCard
                    as={Link}
                    to="/admin"
                    style={adminStyle}
                  >
                    <DashboardIcon sx={{ fontSize: 18 }} />
                    Dashboard
                  </SpotlightCard>
                )}
              </Box>
            )}

            {/* AUTH BUTTONS */}
            {!user ? (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <SpotlightCard as={Link} to="/login" style={navStyle}>
                  <LoginIcon sx={{ fontSize: 18 }} />
                  Login
                </SpotlightCard>

                <SpotlightCard as={Link} to="/register" style={registerStyle}>
                  <PersonAddIcon sx={{ fontSize: 18 }} />
                  Register
                </SpotlightCard>
              </Box>
            ) : (
              <>
                <SpotlightCard
                  as="button"
                  onClick={handleUserMenuOpen}
                  style={userButtonStyle}
                >
                  <Avatar sx={avatarStyle}>
                    {user.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">{user.username}</Typography>
                  <ArrowDropDownIcon />
                </SpotlightCard>

                {/* USER DROPDOWN MENU */}
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 200,
                      background: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    },
                  }}
                >
                  <MenuItem
                    disabled
                    sx={{
                      opacity: 1,
                      '&.Mui-disabled': { opacity: 1 },
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="white">
                        {user.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </MenuItem>

                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                  {isAdmin() && (
                    <MenuItem
                      component={Link}
                      to="/admin"
                      onClick={handleUserMenuClose}
                      sx={{
                        color: '#ec4899',
                        '&:hover': { background: 'rgba(236, 72, 153, 0.1)' },
                      }}
                    >
                      <DashboardIcon sx={{ mr: 1, fontSize: 20 }} />
                      Admin Dashboard
                    </MenuItem>
                  )}

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      color: '#ef4444',
                      '&:hover': { background: 'rgba(239, 68, 68, 0.1)' },
                    }}
                  >
                    <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        ) : (
          /* ================= MOBILE ================= */
          <>
            <IconButton onClick={handleMenuOpen} sx={{ color: 'white' }}>
              <MenuIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  background: 'rgba(15, 23, 42, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'white',
                },
              }}
            >
              {!isAuthPage && user && (
                <>
                  <MenuItem component={Link} to="/" onClick={handleMenuClose}>
                    <MovieIcon sx={{ mr: 1 }} /> Home
                  </MenuItem>
                  <MenuItem component={Link} to="/search" onClick={handleMenuClose}>
                    <SearchIcon sx={{ mr: 1 }} /> Search
                  </MenuItem>
                  {isAdmin() && (
                    <MenuItem component={Link} to="/admin" onClick={handleMenuClose}>
                      <DashboardIcon sx={{ mr: 1 }} /> Dashboard
                    </MenuItem>
                  )}
                </>
              )}

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {!user ? (
                <>
                  <MenuItem component={Link} to="/login" onClick={handleMenuClose}>
                    <LoginIcon sx={{ mr: 1 }} /> Login
                  </MenuItem>
                  <MenuItem component={Link} to="/register" onClick={handleMenuClose}>
                    <PersonAddIcon sx={{ mr: 1 }} /> Register
                  </MenuItem>
                </>
              ) : (
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} /> Logout
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

/* ================= STYLES ================= */

const navStyle = {
  textDecoration: 'none',
  color: 'white',
  padding: '8px 20px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.15)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const registerStyle = {
  ...navStyle,
  background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
  fontWeight: 600,
};

const adminStyle = {
  ...navStyle,
  background: 'rgba(236,72,153,0.15)',
};

const userButtonStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'white',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
};

const avatarStyle = {
  bgcolor: 'primary.main',
  width: 32,
  height: 32,
  fontSize: '0.9rem',
  fontWeight: 700,
};

export default Navbar;
